/**
 * [Phase 2d Stripe - Session B] Post-checkout landing pages.
 *
 * /billing/success - polls /api/stripe/checkout-status/{session_id}
 * until payment_status finalizes (paid / failed / expired) or attempts
 * are exhausted. Shows a confirmation card on paid, retry guidance
 * otherwise.
 *
 * /billing/cancel - informational; one-click return to the previous
 * page or dashboard.
 *
 * Per spec: success/cancel never trust the URL alone - the status poll
 * is the source of truth (webhook + poll are belt-and-suspenders).
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_ATTEMPTS = 10;
const POLL_MS = 2000;

const PRODUCT_LABEL = {
  analytics_draft_access: "PGF Draft Analytics",
  analytics_monthly:      "In-Season PGF Analytics",
  auction_draft_pass:     "Auction Draft Pass",
  auction_draft_pass_extended: "Auction Draft Pass (Extended)",
  // Legacy session A - kept so old success links still render a label.
  analytics_season_pass:  "PGF Analytics - Season Pass",
  auction_additional_league: "Auction Additional League",
};

export function BillingSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "polling", attempts: 0 });

  useEffect(() => {
    if (!sessionId) {
      setStatus({ state: "error", message: "Missing session_id" });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const token = localStorage.getItem("token");
        const r = await fetch(`${API}/api/stripe/checkout-status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (d.payment_status === "paid") {
          setStatus({ state: "paid", data: d });
          return;
        }
        if (d.payment_status === "expired") {
          setStatus({ state: "expired", data: d });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setStatus({ state: "timeout", data: d });
          return;
        }
        setStatus({ state: "polling", attempts });
        setTimeout(poll, POLL_MS);
      } catch (e) {
        if (attempts >= MAX_ATTEMPTS) {
          setStatus({ state: "error", message: e.message });
          return;
        }
        setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="billing-success-page" style={{ background: "#0a0a0a" }}>
      <div className="max-w-md text-center text-white">
        {status.state === "polling" && (
          <>
            <div className="animate-pulse text-[#FF6B00] font-display font-black text-xs uppercase tracking-widest mb-2">Confirming…</div>
            <h1 className="font-display font-black text-2xl mb-2">Hang tight</h1>
            <p className="font-body text-sm opacity-80">We're confirming your payment with Stripe.</p>
          </>
        )}
        {status.state === "paid" && (
          <div data-testid="billing-success-paid">
            <div className="text-emerald-400 font-display font-black text-xs uppercase tracking-widest mb-2">Payment received</div>
            <h1 className="font-display font-black text-3xl mb-3">PGF Analytics activated.</h1>
            <p className="font-body text-sm opacity-80 mb-6">
              {PRODUCT_LABEL[status.data?.metadata?.product] || "Your purchase"} is now active on your account.
            </p>
            <button
              data-testid="billing-success-continue"
              onClick={() => navigate("/dashboard")}
              className="px-5 py-2.5 rounded-md bg-[#FF6B00] hover:bg-[#FF8533] font-display font-black text-sm uppercase tracking-wider text-white shadow-lg"
            >
              Go to Dashboard
            </button>
          </div>
        )}
        {(status.state === "expired" || status.state === "timeout" || status.state === "error") && (
          <div data-testid="billing-success-failed">
            <div className="text-amber-400 font-display font-black text-xs uppercase tracking-widest mb-2">
              {status.state === "expired" ? "Session expired" : status.state === "timeout" ? "Confirmation timed out" : "Hmm"}
            </div>
            <h1 className="font-display font-black text-2xl mb-3">
              {status.state === "expired" ? "Checkout session expired." : "We didn't get a confirmation."}
            </h1>
            <p className="font-body text-sm opacity-80 mb-6">
              If your card was charged, the entitlement will activate when the webhook lands. Refresh in a minute, or contact support.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="px-5 py-2.5 rounded-md border border-[#FF6B00] hover:bg-[#FF6B00]/15 font-display font-black text-sm uppercase tracking-wider text-[#FF6B00]"
            >
              Back to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function BillingCancelPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="billing-cancel-page" style={{ background: "#0a0a0a" }}>
      <div className="max-w-md text-center text-white">
        <div className="text-white/60 font-display font-black text-xs uppercase tracking-widest mb-2">Cancelled</div>
        <h1 className="font-display font-black text-2xl mb-3">No charges made.</h1>
        <p className="font-body text-sm opacity-80 mb-6">
          You closed out of checkout before completing. Come back any time.
        </p>
        <button
          data-testid="billing-cancel-back"
          onClick={() => navigate(-1)}
          className="px-5 py-2.5 rounded-md bg-[#FF6B00] hover:bg-[#FF8533] font-display font-black text-sm uppercase tracking-wider text-white shadow-lg"
        >
          Go Back
        </button>
      </div>
    </div>
  );
}
